'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { app } from '@todesktop/client-core';
import { useEffect, useEffectEvent } from 'react';
import { usePrNavigation } from '@/components/providers/pr-navigation';
import { clientLogger } from '@/lib/client-logger';
import { openInDesktopAppWindow } from '@/lib/desktop-app-navigation';
import { validateHandoffTarget } from '@/lib/desktop-handoff/eligibility';
import { warmPrData } from '@/lib/desktop-pr-warm-up';
import { subscribeToTrayWindowRequests } from '@/lib/desktop-tray-window';
import { getDeepLinkPath } from '@/lib/shared/deep-link-types';
import { useAuth } from '@/providers/auth-provider';

/** The `open-protocol-url` event as ToDesktop's preload (runtime 7.x) delivers it. */
type OpenProtocolUrlEvent = {
  originalURL: string;
  preventDefault: () => void;
};

/**
 * Subscribes to `cubic://` links opened while the app is running and returns a synchronous
 * unsubscribe for effect cleanup. client-core 1.20 mistypes this event (it names the field
 * `originalUrl` and expects the listener to return a Promise), so the cast stays in here.
 */
function subscribeToOpenProtocolUrl(listener: (event: OpenProtocolUrlEvent) => void): () => void {
  const onOpenProtocolUrl = app.on as unknown as (event: 'openProtocolURL', listener: (eventName: string, event: OpenProtocolUrlEvent) => void) => Promise<() => Promise<void>>;
  const subscription = onOpenProtocolUrl('openProtocolURL', (_eventName, event) => listener(event));
  subscription.catch(error => {
    clientLogger.error('Failed to register the desktop deep link receiver', error);
  });
  return () => {
    void subscription.then(unsubscribe => unsubscribe(), () => undefined);
  };
}

/**
 * Opens `cubic://` links to PRs and the inbox with client-side navigation while the desktop app
 * is running, instead of ToDesktop's default full reload of the window. Also opens what the user
 * picks in the tray popover, which runs in its own window.
 */
export function DesktopDeepLinkReceiver() {
  const $ = _c(7);
  const {
    navigate
  } = usePrNavigation();
  const queryClient = useQueryClient();
  const {
    user
  } = useAuth();
  let t0;
  if ($[0] !== navigate || $[1] !== queryClient || $[2] !== user) {
    t0 = target => {
      warmPrData(queryClient, target, user);
      openInDesktopAppWindow(target, href => navigate(href, {
        source: "desktop",
        entrance: "window-reveal"
      })).catch(_temp);
    };
    $[0] = navigate;
    $[1] = queryClient;
    $[2] = user;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const open = useEffectEvent(t0);
  let t1;
  if ($[4] !== open) {
    t1 = () => {
      const unsubscribeFromProtocolUrls = subscribeToOpenProtocolUrl(event => {
        const target_0 = validateHandoffTarget(getDeepLinkPath(event.originalURL));
        if (!target_0) {
          return;
        }
        event.preventDefault();
        open(target_0);
      });
      const unsubscribeFromTrayWindow = subscribeToTrayWindowRequests(path => {
        const target_1 = validateHandoffTarget(path);
        if (target_1) {
          open(target_1);
        }
      });
      return () => {
        unsubscribeFromProtocolUrls();
        unsubscribeFromTrayWindow();
      };
    };
    $[4] = open;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useEffect(t1, t2);
  return null;
}
function _temp(error) {
  return clientLogger.error("Failed to reveal desktop app after opening a deep link", error);
}